import type { TaskRow } from './api'
import type { KpiCadence } from './kpiApi'

/**
 * Urgency bucketing and date formatting.
 *
 * Everything here keys off `days_until`, which Postgres computed in shop-local
 * time. Nothing in this file constructs a Date from the due date in order to
 * decide urgency — see the note in api.ts.
 */

export type BucketKey = 'overdue' | 'today' | 'soon' | 'later' | 'done'

export interface Bucket {
  key: BucketKey
  label: string
  tasks: TaskRow[]
}

/**
 * Matches the reminder rule: overdue or due within 3 days is what earns a
 * Slack DM. Keeping the UI and the notifications on the same threshold means
 * the app never shows "nothing urgent" while a DM says otherwise.
 */
export const SOON_THRESHOLD_DAYS = 3

export function bucketTasks(tasks: TaskRow[]): Bucket[] {
  const overdue: TaskRow[] = []
  const today: TaskRow[] = []
  const soon: TaskRow[] = []
  const later: TaskRow[] = []
  const done: TaskRow[] = []

  for (const t of tasks) {
    if (t.status === 'Completed') done.push(t)
    else if (t.days_until < 0) overdue.push(t)
    else if (t.days_until === 0) today.push(t)
    else if (t.days_until <= SOON_THRESHOLD_DAYS) soon.push(t)
    else later.push(t)
  }

  const all: Bucket[] = [
    { key: 'overdue', label: 'Overdue', tasks: overdue },
    { key: 'today', label: 'Due today', tasks: today },
    { key: 'soon', label: 'Next few days', tasks: soon },
    { key: 'later', label: 'Later', tasks: later },
    { key: 'done', label: 'Done', tasks: done },
  ]
  return all.filter((b) => b.tasks.length > 0)
}

/**
 * Same urgency buckets for the admin's merged list.
 *
 * "Done" here means every assignee has finished, not just the admin — a task
 * three people share is not done because one of them ticked it.
 */
export function bucketBy<T>(
  items: T[],
  daysUntil: (item: T) => number,
  isDone: (item: T) => boolean,
): { key: BucketKey; label: string; items: T[] }[] {
  const overdue: T[] = []
  const today: T[] = []
  const soon: T[] = []
  const later: T[] = []
  const done: T[] = []

  for (const it of items) {
    const d = daysUntil(it)
    if (isDone(it)) done.push(it)
    else if (d < 0) overdue.push(it)
    else if (d === 0) today.push(it)
    else if (d <= SOON_THRESHOLD_DAYS) soon.push(it)
    else later.push(it)
  }

  const all: { key: BucketKey; label: string; items: T[] }[] = [
    { key: 'overdue', label: 'Overdue', items: overdue },
    { key: 'today', label: 'Due today', items: today },
    { key: 'soon', label: 'Next few days', items: soon },
    { key: 'later', label: 'Later', items: later },
    { key: 'done', label: 'Done', items: done },
  ]
  return all.filter((b) => b.items.length > 0)
}

/*
 * Dates. One style everywhere — "10/28/26" — and every date on a card says what
 * it is: "Due 10/28/26" on a task, "Created 10/19/26" on an issue or headline
 * (Scott, 2026-10-22). These are the only date formatters; add to them rather
 * than formatting a date inline somewhere else.
 */

/**
 * "10/28/26" from a YYYY-MM-DD date (a due date, a KPI period start).
 *
 * Deliberately parsed by splitting the string rather than `new Date(ymd)`.
 * `new Date('2026-01-21')` is read as UTC midnight, which renders as Jan 20 for
 * anyone east-of-UTC-negative — i.e. it shows the wrong day in Eastern. This is
 * trap #1 and it has already cost this codebase's sibling project twice.
 */
export function formatDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  if (!y || !m || !d || m > 12) return ymd
  return `${m}/${d}/${String(y).slice(-2)}`
}

/**
 * "10/19/26" for a moment in time (created_at, uploaded_at …), as the shop's
 * calendar sees it. An explicit America/New_York, never the phone's zone or a
 * slice of the UTC string — either way an evening entry shows the next day.
 */
export function shopDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
    })
  } catch {
    return iso
  }
}

/** "10/19/26, 9:30 AM" for a moment where the time matters too (Happened, a meeting's start). */
export function shopDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/**
 * How late or soon a due date is, to follow "Due 10/28/26 · ": "today",
 * "tomorrow", "in 7 days", "3 days overdue". The word "Due" is on the date, so
 * it is not repeated here.
 */
export function relativeDue(daysUntil: number): string {
  if (daysUntil === 0) return 'today'
  if (daysUntil === 1) return 'tomorrow'
  if (daysUntil === -1) return '1 day overdue'
  if (daysUntil < 0) return `${Math.abs(daysUntil)} days overdue`
  return `in ${daysUntil} days`
}


/**
 * Add `n` days to a YYYY-MM-DD string and return YYYY-MM-DD.
 *
 * Pure calendar arithmetic: the date goes in through `Date.UTC` and comes
 * back out through the `getUTC*` getters, so the browser's local zone and its
 * DST transitions never touch it. (`new Date(ymd)` followed by `setDate` would
 * read local time and can land a day out at a DST boundary.)
 */
export function addDays(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number)
  if (!y || !m || !d) return ymd
  const t = new Date(Date.UTC(y, m - 1, d + n))
  const mm = String(t.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(t.getUTCDate()).padStart(2, '0')
  return `${t.getUTCFullYear()}-${mm}-${dd}`
}

/** "9/28" — formatDate minus the year, for the narrow KPI grid columns. */
export function shortDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number)
  if (!y || !m || !d || m > 12) return ymd
  return `${m}/${d}`
}

/**
 * The label for one KPI grid column: "9/28 – 10/4" for a week (Monday start,
 * so the end is start + 6) or just "10/7" for a day.
 */
export function periodColumnLabel(cadence: KpiCadence, start: string): string {
  if (cadence === 'daily') return shortDate(start)
  return `${shortDate(start)} – ${shortDate(addDays(start, 6))}`
}
